import React, { Component } from 'react';
import { connect } from 'react-redux';
import { I18n } from 'react-redux-i18n';
import styled from 'styled-components';
import get from 'lodash/get';

import Header from '../../components/Header';
import Button from '../../components/Button';
import PersonalInformation from './PersonalInformation';
import Preferences from './Preferences';
import Market from './Market';
import ServiceNotification from './ServiceNotification';
import {
  getLanguageUser,
  patchUser,
  resetPassword,
  setLanguageLocale
} from '../../actions/auth';
import { getUserCampaigns } from '../../actions/campaigns';
import { patchUserServices } from '../../actions/services';
import { getUserJournals } from '../../actions/journals';
import { getWines } from '../../actions/wines';
import { getBrands } from '../../actions/brands';

import TasterSubscriptions from './components/TasterSubscriptions';
import WineCompetitors from './components/WineCompetitor';
import UserListCard from './components/UserListCard';
import Spinner from '../../components/Spinner';

import { useCurrencies } from '../../apiTyped/hooks/common.query';
import {
  useUserMe,
  usePositions,
  useUserServices
} from '../../apiTyped/hooks/users.query';

const AdminContainer = styled.div`
  flex: 1;
  padding: 20px;

  @media (max-width: 850px) {
    padding: 16px;
  }

  .adminContentContainer {
    margin-top: 23px;
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-gap: 20px;
    padding-bottom: 30px;

    @media (max-width: 1320px) {
      display: grid;
      grid-template-columns: 1fr;
      grid-gap: 20px;
      padding-bottom: 30px;
    }
  }

  .adminContentContainer > * {
    border-radius: 4px;
    background-color: #ffffff;
  }

  .saveButtonContainer {
    position: fixed;
    top: 40px;
    right: 40px;
    z-index: 3;

    @media (max-width: 850px) {
      top: auto;
      right: auto;
      left: 20px;
      bottom: 30px;
    }
  }
`;

class Admin extends Component {
  constructor(props) {
    super(props);
    this.state = {
      userId: null,
      loading: true,
      firstname: '',
      lastname: '',
      email: '',
      phone: '',
      job: null,
      linkedin: '',
      administrator: ''
    };

    this.handleSubmit = this.handleSubmit.bind(this);
  }

  componentDidMount() {
    const { user } = this.props;
    if (user.id !== null) {
      this.updateUser();
    }

    Promise.all([
      // Prevent from fetching positions if they are already in the store
      this.props.getUserJournals(),
      this.props.getWines(),
      this.props.getUserCampaigns(),
      this.props.getBrands()
    ]).then(() => {
      window.scrollTo(0, 0);
    });
  }

  componentDidUpdate(prevProps) {
    if (
      JSON.stringify(prevProps.user) !== JSON.stringify(this.props.user) ||
      !this.state.userId
    ) {
      this.updateUser();
    }
  }

  updateUser() {
    const { user } = this.props;
    if (user.id === null) {
      return;
    }

    this.setState({
      loading: false,
      userId: user.id,
      firstname: user.firstname,
      lastname: user.lastname,
      email: user.email,
      emailAdmin: user.client.email,
      phone: user.phone,
      job: user.job,
      linkedin: user.linkedin,
      administrator: user.administrator,
      selectedCurrency: user.currency,
      selectedLang: user.language,
      selectedTimezone: user.client.timezone,
      newsletter: user.newsletter,
      newsletterHour: user.newsletterHour,
      password: '',
      errorNewPassword: false
    });
  }

  handleValueChange(field, value) {
    this.setState({
      [field]: value
    });
  }

  passwordVerification() {
    if (
      this.state.password &&
      this.state.reapeatpassword &&
      this.state.password === this.state.reapeatpassword
    ) {
      this.setState({ errorNewPassword: false });
      return true;
    } else {
      this.setState({ errorNewPassword: true });
      return false;
    }
  }

  async handleSubmit() {
    let dataUser = {
      first_name: this.state.firstname,
      last_name: this.state.lastname,
      position: this.state.job,
      linkedin_profile: this.state.linkedin,
      currency_displayed: this.state.selectedCurrency,
      newsletter_hour: this.state.newsletterHour,
      is_newsletter_activated: this.state.newsletter,
      language: this.state.selectedLang,
      email: this.state.email,
      phone: this.state.phone,
      time_zone: this.state.selectedTimezone
    };

    if (this.passwordVerification()) {
      this.props.resetPassword({
        new_password1: this.state.password,
        new_password2: this.state.reapeatpassword
      });
      dataUser.password = this.state.password;
    }

    this.setState({ loading: true });

    await this.props.patchUser(dataUser);

    await this.props.refreshUser();

    const userLanguage = getLanguageUser(dataUser);
    if (this.props.locale !== userLanguage) {
      await this.props.setLanguageLocale(userLanguage);
    }
    this.setState({ loading: false });
  }

  async handleServicesCheckboxes(index) {
    let { services } = this.props;
    let updatedService = services.find((item) => item.id === index);
    updatedService = {
      live_notifications: !updatedService.live_notifications,
      service: updatedService.id,
      user: this.props.user.id,
      id: updatedService.idUserService
    };
    this.setState({ loading: true });
    await this.props.patchUserServices(updatedService);
    this.props.refreshServices();
    this.setState({ loading: false });
  }

  render() {
    const { services, campaigns, user, journals, wines, brands } = this.props;
    const { errorNewPassword } = this.state;

    const isAdmin = get(user, 'administrator', false);
    const { users = [] } = get(user, 'client', {});

    let displayWineShop = user.client.displayWineshops;

    return (
      <AdminContainer>
        <Header title={I18n.t('admin.title')}>
          <div className="saveButtonContainer">
            <Button
              label={I18n.t('admin.save')}
              width="calc(120px + 5vw)"
              textTransform="none"
              onClick={() => this.handleSubmit()}
            />
          </div>
        </Header>
        <div className="adminContentContainer">
          <PersonalInformation
            data={this.state}
            optionsPosition={this.props.positions || []}
            onChange={(field, value) => this.handleValueChange(field, value)}
          />
          <Preferences
            data={this.state}
            currencies={this.props.currencies}
            onChange={(field, value) => this.handleValueChange(field, value)}
            errorNewPassword={errorNewPassword}
            passwordVerification={() => this.passwordVerification()}
          />
          <Market markets={campaigns} displayWineShop={displayWineShop} />
          <WineCompetitors
            wines={wines}
            brands={brands}
            services={services}
            isBranded={get(user, 'client.marqueAnalysis')}
          />
          <ServiceNotification
            data={this.state}
            services_info={services}
            onChangeSelect={(field, value) =>
              this.handleValueChange(field, value)
            }
            onChangeCheckbox={(index) => this.handleServicesCheckboxes(index)}
          />
          <TasterSubscriptions admin={isAdmin} data={journals} />
        </div>
        {isAdmin && (
          <UserListCard data={users} admin={isAdmin} services={services} />
        )}
        <Spinner loading={this.state.loading} />
      </AdminContainer>
    );
  }
}

const Index = (props) => {
  const { user, refetch: refreshUser } = useUserMe(true);
  const { currencies } = useCurrencies();
  const { positions } = usePositions();
  const { services, refetch: refreshServices } = useUserServices();

  return (
    <Admin
      {...props}
      user={user}
      currencies={currencies}
      positions={positions}
      services={services}
      refreshUser={refreshUser}
      refreshServices={refreshServices}
    />
  );
};

const mapStateToProps = (state) => {
  return {
    journals: state.journals,
    wines: state.wines,
    campaigns: state.campaigns,
    brands: state.brands,
    locale: state.i18n.locale
  };
};

const mapDispatchToProps = (dispatch) => ({
  patchUser: async (data) => {
    await dispatch(patchUser(data));
  },
  setLanguageLocale: async (locale) => {
    await dispatch(setLanguageLocale(locale));
  },
  patchUserServices: async (data) => {
    await dispatch(patchUserServices(data));
  },
  getUserJournals: async () => {
    await dispatch(getUserJournals());
  },
  getWines: async () => {
    await dispatch(getWines());
  },
  getBrands: async () => {
    await dispatch(getBrands());
  },
  getUserCampaigns: async () => {
    await dispatch(getUserCampaigns());
  },
  resetPassword: (data) => {
    dispatch(resetPassword(data));
  }
});

export default connect(mapStateToProps, mapDispatchToProps)(Index);
